// /src/components/admin/AdminAnalytics.jsx
// Admin Analytics Dashboard - View platform analytics data
// Requires authentication

import React, { useState, useEffect } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import { useTheme } from '../../themes/ThemeProvider';
import { useFormattingLocale } from '../../i18n/useTranslation';
import { supabase } from '../../lib/supabase';
import { BarChart3, Users, FileText, MessageSquare, RefreshCw } from 'lucide-react';

const AdminAnalytics = ({ onBack, currentUser }) => {
  const theme = useTheme();
  const formattingLocale = useFormattingLocale();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth token from localStorage (avoids hanging supabase.auth.getSession())
      let accessToken = null;
      const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
      if (storageKey) {
        try {
          const storedData = JSON.parse(localStorage.getItem(storageKey) || '{}');
          accessToken = storedData?.access_token;
        } catch (e) {
          console.warn('AdminAnalytics: Failed to parse auth token from localStorage');
        }
      }

      if (!accessToken) {
        setError('You must be logged in to view analytics');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/analytics-admin?days=${days}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch analytics');
      }

      const analyticsData = await response.json();
      setData(analyticsData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  // Header context
  const headerContext = { name: 'Platform Analytics' };

  // Styles - using theme with fallbacks for undefined values
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      backgroundColor: theme?.bg || '#F5F5F5',
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
    },
    card: {
      backgroundColor: theme?.cardBg || 'white',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    cardTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: theme?.text?.secondary || '#666',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    statGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '12px',
    },
    statBox: {
      backgroundColor: theme?.bg || '#F5F5F5',
      borderRadius: '6px',
      padding: '12px',
      textAlign: 'center',
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: theme?.primary || '#024B99',
    },
    statLabel: {
      fontSize: '12px',
      color: theme?.text?.secondary || '#666',
      marginTop: '4px',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
    },
    th: {
      textAlign: 'left',
      padding: '8px',
      borderBottom: `1px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.secondary || '#666',
      fontWeight: '600',
    },
    td: {
      padding: '8px',
      borderBottom: `1px solid ${theme?.border || '#DDD'}`,
      color: theme?.text?.primary || '#1A1A1A',
    },
    periodSelector: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
    },
    periodButton: (active) => ({
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: active ? (theme?.primary || '#024B99') : (theme?.cardBg || 'white'),
      color: active ? '#fff' : (theme?.text?.primary || '#1A1A1A'),
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
    }),
    refreshButton: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: theme?.cardBg || 'white',
      color: theme?.text?.primary || '#1A1A1A',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginLeft: 'auto',
    },
    errorBox: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fca5a5',
      borderRadius: '8px',
      padding: '16px',
      color: '#991b1b',
      textAlign: 'center',
    },
    loadingBox: {
      textAlign: 'center',
      padding: '40px',
      color: theme?.text?.secondary || '#666',
    },
    badge: (type) => {
      const colors = {
        session_start: { bg: '#dbeafe', text: '#1d4ed8' },
        session_end: { bg: '#f3e8ff', text: '#7c3aed' },
        article_read: { bg: '#dcfce7', text: '#15803d' },
        group_joined: { bg: '#fef3c7', text: '#b45309' },
        registration: { bg: '#fce7f3', text: '#be185d' },
        content_published: { bg: '#e0e7ff', text: '#4338ca' },
      };
      const color = colors[type] || { bg: '#f3f4f6', text: '#374151' };
      return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '500',
        backgroundColor: color.bg,
        color: color.text,
      };
    },
  };

  return (
    <div style={styles.container}>
      <HeaderContainer
        context={headerContext}
        onBack={onBack}
      />

      <div style={styles.content}>
        {/* Period Selector */}
        <div style={styles.periodSelector}>
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              style={styles.periodButton(days === d)}
              onClick={() => setDays(d)}
            >
              {d === 1 ? 'Today' : `${d} Days`}
            </button>
          ))}
          <button
            style={styles.refreshButton}
            onClick={fetchAnalytics}
            disabled={loading}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && !data && (
          <div style={styles.loadingBox}>
            Loading analytics...
          </div>
        )}

        {/* Analytics Data */}
        {data && (
          <>
            {/* Summary Stats */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                <BarChart3 size={16} />
                Summary ({data.period.start} to {data.period.end})
              </div>
              <div style={styles.statGrid}>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.total_events}</div>
                  <div style={styles.statLabel}>Total Events</div>
                </div>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.unique_sessions}</div>
                  <div style={styles.statLabel}>Unique Sessions</div>
                </div>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.events_by_type?.session_start || 0}</div>
                  <div style={styles.statLabel}>Session Starts</div>
                </div>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.events_by_type?.registration || 0}</div>
                  <div style={styles.statLabel}>Registrations</div>
                </div>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.events_by_type?.article_read || 0}</div>
                  <div style={styles.statLabel}>Articles Read</div>
                </div>
                <div style={styles.statBox}>
                  <div style={styles.statValue}>{data.summary.events_by_type?.group_joined || 0}</div>
                  <div style={styles.statLabel}>Groups Joined</div>
                </div>
              </div>
            </div>

            {/* Events by Type */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                <FileText size={16} />
                Events by Type
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Event Type</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.summary.events_by_type || {}).map(([type, count]) => (
                    <tr key={type}>
                      <td style={styles.td}>
                        <span style={styles.badge(type)}>{type}</span>
                      </td>
                      <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Device Breakdown */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                <Users size={16} />
                Device Breakdown
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Device</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Events</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.summary.device_breakdown || {}).map(([device, count]) => (
                    <tr key={device}>
                      <td style={styles.td}>{device}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>{count}</td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        {((count / data.summary.total_events) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Recent Events */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                <MessageSquare size={16} />
                Recent Events (Last 20)
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Time</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Session</th>
                      <th style={styles.th}>Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recent_events || []).map((event) => (
                      <tr key={event.id}>
                        <td style={styles.td}>
                          {new Date(event.created_at).toLocaleString(formattingLocale)}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(event.event_type)}>{event.event_type}</span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '11px' }}>
                          {event.session_hash}
                        </td>
                        <td style={styles.td}>{event.device_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <FooterContainer />
    </div>
  );
};

export default AdminAnalytics;
